import { useState, useEffect, useRef } from 'react';
import { expenses, budgets } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  Plus, Receipt, DollarSign, Trash2, TrendingUp, TrendingDown, 
  BarChart3, PieChart, Package, Users, Megaphone, Briefcase, Home, Laptop, MoreHorizontal,
  Upload, Download, FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  operaciones: Package,
  nomina: Users,
  marketing: Megaphone,
  servicios: Briefcase,
  suministros: Package,
  alquiler: Home,
  tecnologia: Laptop,
  otros: MoreHorizontal,
};

const CATEGORY_COLORS = {
  operaciones: 'bg-blue-100 text-blue-700',
  nomina: 'bg-purple-100 text-purple-700',
  marketing: 'bg-pink-100 text-pink-700',
  servicios: 'bg-amber-100 text-amber-700',
  suministros: 'bg-teal-100 text-teal-700',
  alquiler: 'bg-indigo-100 text-indigo-700',
  tecnologia: 'bg-cyan-100 text-cyan-700',
  otros: 'bg-gray-100 text-gray-700',
};

function CreateExpenseDialog({ open, onOpenChange, onSubmit, categories }) {
  const [formData, setFormData] = useState({
    supplier_name: '',
    supplier_rnc: '',
    ncf: '',
    category: 'operaciones',
    description: '',
    amount: '',
    tax_amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    due_date: '',
    notes: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      amount: parseFloat(formData.amount) || 0,
      tax_amount: parseFloat(formData.tax_amount) || 0,
    };
    onSubmit(data);
    setFormData({
      supplier_name: '',
      supplier_rnc: '',
      ncf: '',
      category: 'operaciones',
      description: '',
      amount: '',
      tax_amount: '',
      expense_date: new Date().toISOString().split('T')[0],
      due_date: '',
      notes: '',
    });
  };

  const subtotal = parseFloat(formData.amount) || 0;
  const tax = parseFloat(formData.tax_amount) || 0;
  const total = subtotal + tax;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Registrar Gasto</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Proveedor *</Label>
              <Input
                value={formData.supplier_name}
                onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                placeholder="Nombre del proveedor"
                required
                data-testid="expense-supplier-input"
              />
            </div>
            <div className="space-y-2">
              <Label>RNC Proveedor</Label>
              <Input
                value={formData.supplier_rnc}
                onChange={(e) => setFormData({ ...formData, supplier_rnc: e.target.value })}
                placeholder="000-00000-0"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>NCF del Proveedor</Label>
              <Input
                value={formData.ncf}
                onChange={(e) => setFormData({ ...formData, ncf: e.target.value })}
                placeholder="B0100000001"
              />
            </div>
            <div className="space-y-2">
              <Label>Categoría *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger data-testid="expense-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(categories).map(([key, cat]) => (
                    <SelectItem key={key} value={key}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Descripción *</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descripción del gasto"
              required
              data-testid="expense-description-input"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Monto *</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                min="0"
                step="0.01"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>ITBIS</Label>
              <Input
                type="number"
                value={formData.tax_amount}
                onChange={(e) => setFormData({ ...formData, tax_amount: e.target.value })}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
            <div className="space-y-2">
              <Label>Total</Label>
              <div className="h-10 px-3 py-2 bg-muted rounded-md font-mono font-semibold">
                {formatCurrency(total)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Fecha del Gasto</Label>
              <Input
                type="date"
                value={formData.expense_date}
                onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha de Vencimiento</Label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Notas adicionales..."
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" data-testid="expense-submit-btn">
              Registrar Gasto
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateBudgetDialog({ open, onOpenChange, onSubmit, categories }) {
  const [formData, setFormData] = useState({
    name: '',
    category: 'operaciones',
    period: 'monthly',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    budgeted_amount: '',
    notes: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      budgeted_amount: parseFloat(formData.budgeted_amount) || 0,
      month: formData.period === 'monthly' ? parseInt(formData.month) : null,
      quarter: formData.period === 'quarterly' ? Math.ceil(parseInt(formData.month) / 3) : null,
    };
    onSubmit(data);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Crear Presupuesto</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nombre *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ej: Marketing Q1 2026"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Categoría *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(categories).map(([key, cat]) => (
                    <SelectItem key={key} value={key}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Período *</Label>
              <Select
                value={formData.period}
                onValueChange={(value) => setFormData({ ...formData, period: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensual</SelectItem>
                  <SelectItem value="quarterly">Trimestral</SelectItem>
                  <SelectItem value="yearly">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Año *</Label>
              <Input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                min="2020"
                max="2030"
              />
            </div>
            {formData.period === 'monthly' && (
              <div className="space-y-2">
                <Label>Mes</Label>
                <Select
                  value={formData.month.toString()}
                  onValueChange={(value) => setFormData({ ...formData, month: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'].map((m, i) => (
                      <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Monto Presupuestado *</Label>
            <Input
              type="number"
              value={formData.budgeted_amount}
              onChange={(e) => setFormData({ ...formData, budgeted_amount: e.target.value })}
              placeholder="0.00"
              min="0"
              step="0.01"
              required
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">
              Crear Presupuesto
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BudgetComparisonCard({ item }) {
  const isOver = item.status === 'over';
  const percentage = item.budgeted > 0 ? Math.min((item.actual / item.budgeted) * 100, 150) : 0;
  
  return (
    <div className="p-4 rounded-lg border hover:shadow-sm transition-all">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="font-medium">{item.name}</p>
          <p className="text-sm text-muted-foreground">{item.category_name}</p>
        </div>
        <Badge className={isOver ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}>
          {isOver ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
          {item.variance_percent.toFixed(1)}%
        </Badge>
      </div>
      
      <Progress value={percentage} className={`h-2 ${isOver ? '[&>div]:bg-rose-500' : '[&>div]:bg-emerald-500'}`} />
      
      <div className="flex justify-between mt-2 text-sm">
        <span className="text-muted-foreground">
          Actual: <span className="font-mono font-medium">{formatCurrency(item.actual)}</span>
        </span>
        <span className="text-muted-foreground">
          Budget: <span className="font-mono font-medium">{formatCurrency(item.budgeted)}</span>
        </span>
      </div>
    </div>
  );
}

export default function Costs() {
  const [expensesList, setExpensesList] = useState([]);
  const [budgetsList, setBudgetsList] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [createExpenseOpen, setCreateExpenseOpen] = useState(false);
  const [createBudgetOpen, setCreateBudgetOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [expRes, budRes, catRes, compRes] = await Promise.all([
        expenses.getAll(),
        budgets.getAll(),
        expenses.getCategories(),
        budgets.getComparison()
      ]);
      setExpensesList(expRes.data || []);
      setBudgetsList(budRes.data || []);
      setCategories(catRes.data || {});
      setComparison(compRes.data || null);
    } catch (err) {
      console.error('Error loading costs data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await expenses.downloadTemplate();
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_gastos_transtar.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Plantilla descargada');
    } catch (err) {
      toast.error('Error al descargar plantilla');
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const res = await expenses.importExcel(file);
      if (res.data.success) {
        toast.success(`${res.data.imported_count} gastos importados exitosamente`);
        if (res.data.errors?.length > 0) {
          toast.warning(`${res.data.errors.length} errores encontrados`);
        }
        setImportDialogOpen(false);
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al importar gastos');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCreateExpense = async (data) => {
    try {
      await expenses.create(data);
      toast.success('Gasto registrado');
      setCreateExpenseOpen(false);
      loadData();
    } catch (err) {
      toast.error('Error al registrar gasto');
    }
  };

  const handleCreateBudget = async (data) => {
    try {
      await budgets.create(data);
      toast.success('Presupuesto creado');
      loadData();
    } catch (err) {
      toast.error('Error al crear presupuesto');
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    try {
      await expenses.delete(id);
      toast.success('Gasto eliminado');
      loadData();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await expenses.update(id, { status: 'paid' });
      toast.success('Gasto marcado como pagado');
      loadData();
    } catch (err) {
      toast.error('Error al actualizar');
    }
  };

  // Filter expenses
  const filteredExpenses = selectedCategory === 'all' 
    ? expensesList 
    : expensesList.filter(e => e.category === selectedCategory);

  // Calculate totals
  const totalExpenses = expensesList.reduce((sum, e) => sum + (e.total || 0), 0);
  const pendingExpenses = expensesList.filter(e => e.status === 'pending').reduce((sum, e) => sum + (e.total || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="costs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">Costos y Gastos</h1>
          <p className="text-muted-foreground">Registro de facturas de proveedores y presupuestos</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportDialogOpen(true)} data-testid="import-expenses-btn">
            <Upload className="h-4 w-4 mr-2" />
            Importar Excel
          </Button>
          <Button variant="outline" onClick={() => setCreateBudgetOpen(true)}>
            <BarChart3 className="h-4 w-4 mr-2" />
            Nuevo Presupuesto
          </Button>
          <Button onClick={() => setCreateExpenseOpen(true)} data-testid="create-expense-btn">
            <Plus className="h-4 w-4 mr-2" />
            Registrar Gasto
          </Button>
        </div>
      </div>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-primary" />
              Importar Gastos desde Excel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
              <p className="text-sm text-rose-800 mb-3">
                Descarga la plantilla Excel, llénala con tus gastos/facturas de proveedores, y súbela aquí.
              </p>
              <Button variant="outline" onClick={handleDownloadTemplate} className="w-full gap-2">
                <Download className="h-4 w-4" />
                Descargar Plantilla
              </Button>
            </div>
            
            <div className="space-y-2">
              <Label>Archivo Excel</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportExcel}
                disabled={importing}
                data-testid="expense-excel-input"
              />
              <p className="text-xs text-muted-foreground">
                Solo archivos .xlsx o .xls
              </p>
            </div>

            {importing && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                Procesando archivo...
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-rose-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-rose-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Gastos</p>
                <p className="text-2xl font-bold font-mono">{formatCurrency(totalExpenses)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Receipt className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Por Pagar</p>
                <p className="text-2xl font-bold font-mono">{formatCurrency(pendingExpenses)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <PieChart className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Presupuestos</p>
                <p className="text-2xl font-bold">{budgetsList.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="expenses" className="space-y-4">
        <TabsList>
          <TabsTrigger value="expenses">Gastos</TabsTrigger>
          <TabsTrigger value="comparison">Presupuesto vs Real</TabsTrigger>
          <TabsTrigger value="budgets">Presupuestos</TabsTrigger>
        </TabsList>

        <TabsContent value="expenses" className="space-y-4">
          {/* Category Filter */}
          <div className="flex gap-2 flex-wrap">
            <Button 
              variant={selectedCategory === 'all' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setSelectedCategory('all')}
            >
              Todos
            </Button>
            {Object.entries(categories).map(([key, cat]) => {
              const Icon = CATEGORY_ICONS[key] || MoreHorizontal;
              return (
                <Button 
                  key={key}
                  variant={selectedCategory === key ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setSelectedCategory(key)}
                  className="gap-1"
                >
                  <Icon className="h-3 w-3" />
                  {cat.name}
                </Button>
              );
            })}
          </div>

          {/* Expenses Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExpenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No hay gastos registrados
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredExpenses.map((expense) => {
                      const Icon = CATEGORY_ICONS[expense.category] || MoreHorizontal;
                      return (
                        <TableRow key={expense.expense_id}>
                          <TableCell className="font-mono text-sm">{expense.expense_number}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{expense.supplier_name}</p>
                              {expense.supplier_rnc && (
                                <p className="text-xs text-muted-foreground">RNC: {expense.supplier_rnc}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${CATEGORY_COLORS[expense.category]} gap-1`}>
                              <Icon className="h-3 w-3" />
                              {categories[expense.category]?.name || expense.category}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">{expense.description}</TableCell>
                          <TableCell>{formatDate(expense.expense_date)}</TableCell>
                          <TableCell className="text-right font-mono font-semibold">
                            {formatCurrency(expense.total)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={expense.status === 'paid' ? 'success' : 'warning'}>
                              {expense.status === 'paid' ? 'Pagado' : 'Pendiente'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {expense.status !== 'paid' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleMarkPaid(expense.expense_id)}
                                >
                                  Pagar
                                </Button>
                              )}
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleDeleteExpense(expense.expense_id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="comparison" className="space-y-4">
          {comparison && comparison.comparison.length > 0 ? (
            <>
              {/* Summary */}
              <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-sm opacity-80">Período</p>
                      <p className="text-xl font-bold">{comparison.period}</p>
                    </div>
                    <div>
                      <p className="text-sm opacity-80">Presupuestado</p>
                      <p className="text-xl font-bold font-mono">{formatCurrency(comparison.totals.budgeted)}</p>
                    </div>
                    <div>
                      <p className="text-sm opacity-80">Real</p>
                      <p className="text-xl font-bold font-mono">{formatCurrency(comparison.totals.actual)}</p>
                    </div>
                    <div>
                      <p className="text-sm opacity-80">Variación</p>
                      <p className={`text-xl font-bold font-mono ${comparison.totals.variance >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {comparison.totals.variance >= 0 ? '+' : ''}{formatCurrency(comparison.totals.variance)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Comparison by Category */}
              <div className="grid gap-4 md:grid-cols-2">
                {comparison.comparison.map((item) => (
                  <BudgetComparisonCard key={item.budget_id} item={item} />
                ))}
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No hay presupuestos configurados para comparar</p>
                <Button variant="outline" className="mt-4" onClick={() => setCreateBudgetOpen(true)}>
                  Crear Presupuesto
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="budgets" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead>Período</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {budgetsList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No hay presupuestos creados
                      </TableCell>
                    </TableRow>
                  ) : (
                    budgetsList.map((budget) => (
                      <TableRow key={budget.budget_id}>
                        <TableCell className="font-medium">{budget.name}</TableCell>
                        <TableCell>
                          <Badge className={CATEGORY_COLORS[budget.category]}>
                            {categories[budget.category]?.name || budget.category}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {budget.period === 'monthly' && `${budget.year}-${budget.month?.toString().padStart(2, '0')}`}
                          {budget.period === 'quarterly' && `${budget.year} Q${budget.quarter}`}
                          {budget.period === 'yearly' && budget.year}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {formatCurrency(budget.budgeted_amount)}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              if (window.confirm('¿Eliminar este presupuesto?')) {
                                budgets.delete(budget.budget_id).then(loadData);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <CreateExpenseDialog
        open={createExpenseOpen}
        onOpenChange={setCreateExpenseOpen}
        onSubmit={handleCreateExpense}
        categories={categories}
      />

      <CreateBudgetDialog
        open={createBudgetOpen}
        onOpenChange={setCreateBudgetOpen}
        onSubmit={handleCreateBudget}
        categories={categories}
      />
    </div>
  );
}
